/**
 * LazyArtworkCard - Artwork card with lazy loading
 * 
 * Uses IntersectionObserver to only load images when the card
 * is near the viewport, reducing initial load and memory usage.
 */
"use client";

import { useState, useCallback, memo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCrown } from "@fortawesome/free-solid-svg-icons";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { ArtworkHoverOverlay } from "@/components/ui/ArtworkHoverOverlay";
import clsx from "clsx";

// ============================================================================
// Types
// ============================================================================

export interface LazyArtworkCardProps {
  /** Artwork data */
  artwork: {
    id: string;
    title: string;
    image: string;
    thumbnailUrl?: string;
    isPremium?: boolean;
    isFree?: boolean;
    format?: string;
    tags?: readonly string[];
    [key: string]: any;
  };
  /** Click handler */
  onClick: (artwork: any) => void;
  /** Additional CSS classes */
  className?: string;
  /** Root margin for intersection observer (default: "200px") */
  rootMargin?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Artwork card with lazy image loading
 * 
 * @example
 * ```tsx
 * <LazyArtworkCard
 *   artwork={artwork}
 *   onClick={() => setSelectedArtwork(artwork)}
 * />
 * ```
 */
function LazyArtworkCardComponent({
  artwork,
  onClick,
  className,
  rootMargin = "200px"
}: LazyArtworkCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Observe when card enters viewport (with 200px margin for preloading)
  const { ref, isIntersecting } = useIntersectionObserver<HTMLElement>({
    rootMargin,
    triggerOnce: true, // Only trigger once - no need to unload
    threshold: 0
  });

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(true); // Stop showing skeleton
  }, []);

  const handleClick = useCallback(() => {
    onClick(artwork);
  }, [onClick, artwork]);

  const imageUrl = artwork.thumbnailUrl || artwork.image;

  return (
    <article
      ref={ref}
      onClick={handleClick}
      className={clsx(
        "group relative mb-3 overflow-hidden rounded-lg border border-white/10 bg-brand-surface shadow-soft",
        "transition-transform hover:-translate-y-1 hover:border-brand-primary/30 cursor-pointer",
        className
      )}
    >
      <div className="relative">
        {/* Premium badge - always visible */}
        {artwork.isPremium && (
          <span className="absolute top-2 left-2 z-20 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-gradient text-white shadow-soft">
            <FontAwesomeIcon icon={faCrown} className="h-3 w-3" />
          </span>
        )}

        {/* Image container */}
        <div className="relative w-full">
          {/* Skeleton placeholder - shows while loading */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-white/5 animate-pulse">
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            </div>
          )}

          {/* Actual image - only rendered when in viewport */}
          {isIntersecting && !imageError && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={imageUrl}
              alt={`Arte ${artwork.title}`}
              className={clsx(
                "h-full w-full object-cover transition duration-500 group-hover:scale-105",
                imageLoaded ? "opacity-100" : "opacity-0"
              )}
              loading="lazy"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          )}

          {/* Error state */}
          {imageError && (
            <div className="flex items-center justify-center h-40 bg-white/5 text-white/30">
              <span className="text-xs">Erro ao carregar</span>
            </div>
          )}

          {/* Minimum height to prevent layout shift */}
          {!imageLoaded && !imageError && (
            <div className="w-full" style={{ paddingBottom: "100%" }} />
          )}
        </div>

        {/* Hover overlay - only when image is loaded */}
        {imageLoaded && !imageError && (
          <ArtworkHoverOverlay 
            artwork={artwork as any} 
            onVisualize={handleClick} 
          />
        )}
      </div>
    </article>
  );
}

// Memoize to prevent unnecessary re-renders in large lists
export const LazyArtworkCard = memo(LazyArtworkCardComponent);

export default LazyArtworkCard;
